import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import browserHistory, { registerNavigationBlocker } from '~/history';

interface IUseNavigationDetectorOptions {
  when?: boolean;
  onNavigationAttempt?: (targetLocation: any, action: string) => void;
  onBrowserUnload?: () => void;
}

export const useNavigationDetector = ({
  when = false,
  onNavigationAttempt,
  onBrowserUnload,
}: IUseNavigationDetectorOptions = {}) => {
  const location = useLocation();
  const unblockRef = useRef<any>(null);
  const isBlockingRef = useRef(false);
  const pendingNavigationRef = useRef<{ location: any; action: string } | null>(null);

  // Helper to get a unique key for a location (pathname + search + hash)
  const getLocationKey = (loc: any) =>
    (loc?.pathname || '') + (loc?.search || '') + (loc?.hash || '');

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (when) {
        onBrowserUnload?.();
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    },
    [when, onBrowserUnload],
  );

  const allowNavigation = useCallback(() => {
    isBlockingRef.current = true;
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
  }, []);

  const navigateToPending = useCallback(() => {
    const pending = pendingNavigationRef.current;

    if (pending) {
      const { location: targetLocation, action } = pending;

      // Clear the pending navigation first
      pendingNavigationRef.current = null;

      // Allow navigation by setting blocking to true and removing the block
      isBlockingRef.current = true;

      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }

      // Use setTimeout to ensure the navigation happens after the current execution
      setTimeout(() => {
        if (action === 'REPLACE') {
          browserHistory.replace(
            targetLocation.pathname + targetLocation.search + targetLocation.hash,
            targetLocation.state,
          );
        } else if (action === 'POP') {
          browserHistory.back();
        } else {
          browserHistory.push(
            targetLocation.pathname + targetLocation.search + targetLocation.hash,
            targetLocation.state,
          );
        }
      }, 0);
    } else {
      console.warn(
        'No pending navigation found. Make sure to call this only after onNavigationAttempt.',
      );
    }
  }, []);

  const clearPendingNavigation = useCallback(() => {
    pendingNavigationRef.current = null;
  }, []);

  const blockNavigation = useCallback(() => {
    isBlockingRef.current = false;
  }, []);

  useEffect(() => {
    let unregisterFromHistory: (() => void) | undefined;

    if (when && !unblockRef.current) {
      // Handle browser navigation (back/forward buttons, refresh, close tab)
      window.addEventListener('beforeunload', handleBeforeUnload);

      // Handle programmatic navigation within the app
      const unblock = browserHistory.block((tx: any) => {
        // If the navigation is to the same location, allow it
        if (getLocationKey(tx.location) === getLocationKey(location)) {
          return false; // Allow navigation
        }

        if (isBlockingRef.current) {
          // Navigation is explicitly allowed
          return false; // Allow navigation
        }

        // Store the pending navigation
        pendingNavigationRef.current = { location: tx.location, action: tx.action };

        // Always call onNavigationAttempt for each attempt
        onNavigationAttempt?.(tx.location, tx.action);
        return true; // Block navigation and wait for user decision
      });

      unblockRef.current = unblock;

      // Register this blocker so it can be disabled during force navigation
      unregisterFromHistory = registerNavigationBlocker(unblock);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        if (unblockRef.current) {
          unblockRef.current();
          unblockRef.current = null;
        }
        unregisterFromHistory?.();
      };
    } else if (!when) {
      // Clean up when `when` becomes false
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      unregisterFromHistory?.();
    }
  }, [when, handleBeforeUnload, onNavigationAttempt, location]);

  // Reset to blocking state when location changes
  useEffect(() => {
    isBlockingRef.current = false;
    pendingNavigationRef.current = null; // Also clear pending navigation on location change
  }, [location.pathname, location.search, location.hash]);

  return {
    allowNavigation,
    blockNavigation,
    navigateToPending,
    clearPendingNavigation,
  };
};

export default useNavigationDetector;
